"use client";

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LiaNotification } from "./components/lia-notification";
import { useLiaNotificationCache } from "./hooks/useLiaNotificationCache";
import { useAuth } from "@/core/components/auth/store/auth";
import { ChatWindow } from "./components/chat-window";
import { ChatInput } from "./components/chat-input";
import { ChatbotProps } from "./types";
import { useActionsTrack } from "@/core/services/menu-analytics";
import { useIsMobile } from "@/core/hooks/useIsMobile";
import {
  getNotificationForPath,
  getScreenKey,
} from "./constants/liaNotifications";
import { saveChatState } from "@/core/utils/chatbot-storage";
import { ConversationHistoryPanel } from "./components/conversation-history-panel";
import { useChatSession } from "./hooks/useChatSession";
import { ChatSessionMessages } from "./components/chat-session-messages";

export default function FloatingChatbot({
  className,
  isOpen: controlledOpen,
  onOpenChange,
}: ChatbotProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledOpen ?? uncontrolledOpen;
  const setIsOpen = onOpenChange ?? setUncontrolledOpen;
  const { onActionClick } = useActionsTrack();
  const isMobile = useIsMobile();

  const { userInfo } = useAuth();
  const appUserId = userInfo?.user?.id;
  const { pathname } = useLocation();
  const notificationConfig = getNotificationForPath(pathname);
  const screenKey = getScreenKey(pathname);
  const [hasOpenedNotification, markNotificationOpened] =
    useLiaNotificationCache(appUserId, screenKey);
  const [translate] = useTranslation(["core"]);
  const firstName = userInfo?.user?.first_name || "";
  const notificationMessage =
    (translate(notificationConfig.messageKey, {
      firstName: firstName || "você",
    }) as string) || "Oi! Como posso ajudar?";

  const session = useChatSession({ isActive: isOpen });
  const {
    inputValue,
    setInputValue,
    isLoading,
    hydrated,
    conversations,
    activeConversationId,
    isHistoryOpen,
    setIsHistoryOpen,
    sendMessage,
    handleEnter,
    handleNewConversation,
    handleOpenHistory,
    handleCloseHistory,
    handleSelectConversation,
    handleRemoveConversation,
    setExtractionResult,
    setExtractionConfirmed,
  } = session;

  // Prevenir scroll do body quando chat estiver aberto no mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isMobile, isOpen]);

  const handleToggle = () => {
    onActionClick("CHATBOT_CLICK_IA_AGENT", "CHATBOT");
    const next = !isOpen;
    setIsHistoryOpen(false);
    setIsOpen(next);
  };

  return (
    <>
      {/* --- Toggle / botão flutuante --- */}
      <LiaNotification
        isOpen={isOpen}
        onToggle={handleToggle}
        className={className}
        notificationMessage={notificationMessage}
        hasOpenedNotification={hasOpenedNotification}
        onNotificationClick={() => {
          markNotificationOpened();
          onActionClick("CLICK_IA_CHAT_NOTIFICATION", "CHATBOT", {
            notification: notificationMessage,
          });
          onActionClick("CHATBOT_CLICK_IA_AGENT", "CHATBOT");
          setIsOpen(true);
          setIsHistoryOpen(false);
          const actionMessage = translate(
            notificationConfig.actionMessageKey,
          ) as string;
          sendMessage(actionMessage, actionMessage);
        }}
      />

      {/* --- Janela do chat --- */}
      {isOpen && (
        <ChatWindow
          onClose={() => {
            setIsOpen(false);
            setIsHistoryOpen(false);
            saveChatState(false);
          }}
          onNewConversation={() => handleNewConversation("header")}
          isHistoryOpen={isHistoryOpen}
          onOpenHistory={handleOpenHistory}
          onCloseHistory={handleCloseHistory}
          body={
            isHistoryOpen ? (
              <ConversationHistoryPanel
                conversations={conversations}
                activeConversationId={activeConversationId}
                onNewConversation={() => handleNewConversation("history_panel")}
                onSelectConversation={handleSelectConversation}
                onRemoveConversation={handleRemoveConversation}
              />
            ) : (
              <ChatSessionMessages session={session} />
            )
          }
          footer={
            isHistoryOpen ? null : (
              <div className="pb-3">
                <ChatInput
                  value={inputValue}
                  onChange={setInputValue}
                  onSend={sendMessage}
                  onEnter={handleEnter}
                  variant="action-center"
                  onNewConversation={() => handleNewConversation("header")}
                  disabled={isLoading || !hydrated}
                  allowedDomains={userInfo?.league?.domains ?? []}
                  onFileParsed={(result) => {
                    setExtractionResult(result);
                    setExtractionConfirmed(false);
                  }}
                />
              </div>
            )
          }
        />
      )}
    </>
  );
}
